import React, { useMemo, useState, useEffect } from 'react';
import { ChevronFirst, ChevronLeft, ChevronRight, ChevronLast, ChevronUp } from 'lucide-react';
import { formatNumber } from '../../utils/formatting';
import './Pagination.css';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  itemsPerPage, 
  setItemsPerPage, 
  setCurrentPage,
  startItem,
  endItem,
  totalItems
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const tableContainers = document.querySelectorAll('.main-table-scroll-container');
    tableContainers.forEach(container => {
      if (container) {
        container.scrollTop = 0;
      }
    });
  }, [currentPage, itemsPerPage]);
  
  const rowOptions = useMemo(() => {
    const baseRowOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
    
    if (totalItems <= 20) {
      const validOptions = baseRowOptions.filter(size => size <= totalItems);
      
      if (![5, 10, 15].includes(totalItems)) {
        return [...validOptions, totalItems];
      }
      
      return validOptions;
    }
    
    return baseRowOptions;
  }, [totalItems]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.rows-menu') && !event.target.closest('.rows-menu-button')) {
        setMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleRowOptionClick = (size) => {
    if (itemsPerPage === size) return;

    setItemsPerPage(Number(size));
    setCurrentPage(1);
    setMenuOpen(false);
  };

  return (
    <div className="pagination-container">
      <div className="pagination-left">
        <div className="rows-menu-container">
          <button 
            className={`rows-menu-button ${menuOpen ? 'menu-open' : ''}`} 
            onClick={toggleMenu}
            disabled={totalItems <= 5}
          >
            {itemsPerPage === totalItems && totalItems <= 20 ? 'All' : itemsPerPage} rows <ChevronUp size={16} />
          </button>
          {menuOpen && (
            <div className="rows-menu">
              {rowOptions.map(size => (
                <button 
                  key={size} 
                  className={itemsPerPage === size ? 'active' : ''}
                  onClick={() => handleRowOptionClick(size)}
                >
                  {size === totalItems && totalItems <= 20 ? 'All' : `${size}`} rows
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="pagination-center">
        <span className="results-info">
          {startItem} to {endItem} of <strong>{formatNumber(totalItems)}</strong>{' '}
          {totalItems === 1 ? 'result' : 'results'}
        </span>
      </div>

      <div className="pagination-right">
        <button 
          onClick={() => {
            if (currentPage !== 1) setCurrentPage(1);
          }}
          className={`pagination-link ${currentPage === 1 ? 'disabled' : ''}`}
          aria-label="First page"
          disabled={currentPage === 1}
        >
          <ChevronFirst size={16} />
        </button>
        <button 
          onClick={() => {
            if (currentPage !== 1) setCurrentPage(prev => Math.max(prev - 1, 1));
          }}
          className={`pagination-link ${currentPage === 1 ? 'disabled' : ''}`}
          aria-label="Previous page"
          disabled={currentPage === 1}
        >
          <ChevronLeft size={16} />
        </button>
        <span className="current-page">{formatNumber(currentPage)} of {formatNumber(totalPages)}</span>
        <button 
          onClick={() => {
            if (currentPage !== totalPages) setCurrentPage(prev => Math.min(prev + 1, totalPages));
          }}
          className={`pagination-link ${currentPage === totalPages ? 'disabled' : ''}`}
          aria-label="Next page"
          disabled={currentPage === totalPages}
        >
          <ChevronRight size={16} />
        </button>
        <button 
          onClick={() => {
            if (currentPage !== totalPages) setCurrentPage(totalPages);
          }}
          className={`pagination-link ${currentPage === totalPages ? 'disabled' : ''}`}
          aria-label="Last page"
          disabled={currentPage === totalPages}
        >
          <ChevronLast size={16} />
        </button>
      </div>
    </div>
  );
};

export default Pagination;